import { useState } from "react";
import { Link } from "react-router";

import { Layers, LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import { AddToGroupDialog } from "@/pages/events/components/add-to-group-dialog";
import type { EventGroupRef } from "@/types/event-group";

export type EventGroupLineProps = {
  eventId: string;
  /** The event's name, which a refusal of the add that names the event is shown with. */
  eventLabel?: string;
  /** The group the event carries. A ref with no id is no group: the backend's ref can come half empty. */
  group?: EventGroupRef | null;
  /** A group write went through: the page re-reads the event, so the line and the form agree. */
  onChanged?: () => void;
  /**
   * The page holds unsaved edits. Re-reading the event rebuilds the form from the server, which would
   * drop them, so both buttons wait until the curator saves.
   */
  locked?: boolean;
};

const LEAVE_FAILED =
  "Could not take the event out of the group. Try again in a moment.";

/** The confirm behind Leave group. The event stays in Mood; it only stops belonging to the group. */
function LeaveGroupDialog({
  eventId,
  groupId,
  groupName,
  disabled,
  onLeft,
}: {
  eventId: string;
  groupId: string;
  groupName: string;
  disabled: boolean;
  onLeft: () => void;
}) {
  const { removeEvents } = useEventGroupRepository();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setRunning(true);
    setError(null);
    try {
      const result = await removeEvents(groupId, [eventId]);
      if (!result.ok) {
        setError(result.message);
        setRunning(false);
        return;
      }
      setRunning(false);
      setOpen(false);
      onLeft();
    } catch (caught) {
      setError(toHumanErrorMessage(caught) || LEAVE_FAILED);
      setRunning(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (running) return;
        if (next) setError(null);
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled}>
          <LogOut className="size-4" />
          Leave group
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Take this event out of the group?</DialogTitle>
          <DialogDescription>
            {`The event leaves "${groupName}". It stays in Mood, and you can add it to a group again.`}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={running}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={running}
            onClick={() => void confirm()}
          >
            Leave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * An event's group, on the event page's summary. In a group: the group's name as a link to its
 * page, and Leave group. In none: Add to a group. The event's own save never carries the group, so
 * these two buttons are the only way an event's membership changes here.
 */
export function EventGroupLine({
  eventId,
  eventLabel,
  group,
  onChanged,
  locked = false,
}: EventGroupLineProps) {
  const groupId = group?.id || null;
  const groupName = group?.name?.trim() || "Unnamed group";
  const changed = () => onChanged?.();

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <Layers className="h-4 w-4 text-muted-foreground" />
        {groupId ? (
          <>
            <Link
              to={`/event-groups/show/${encodeURIComponent(groupId)}`}
              className="text-sm font-medium hover:underline"
            >
              {`Group: ${groupName}`}
            </Link>
            <LeaveGroupDialog
              eventId={eventId}
              groupId={groupId}
              groupName={groupName}
              disabled={locked}
              onLeft={changed}
            />
          </>
        ) : (
          <>
            <span className="text-sm text-muted-foreground">
              Not in a group
            </span>
            <AddToGroupDialog
              eventId={eventId}
              eventLabel={eventLabel}
              disabled={locked}
              onAdded={changed}
            />
          </>
        )}
      </div>
      {locked && (
        <p className="text-xs text-muted-foreground">
          Save or discard your changes before changing the group.
        </p>
      )}
    </div>
  );
}
